import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ProductCard } from "../components/ProductCard";
import { FilterSidebar } from "../components/FilterSidebar";
import { ChatbotWidget } from "../components/ChatbotWidget";
import { useToggle } from "../contexts/ToggleContext";
import { api } from "../lib/api";

export default function CategoryPage() {
    const { slug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const filtersOn = useToggle("catalog_filters");

    const [allProducts, setAllProducts] = useState([]); // for filter facets
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState(null);
    const sort = searchParams.get("sort") || "new";

    const filters = useMemo(() => {
        const obj = {};
        for (const [k, v] of searchParams.entries()) {
            if (k === "sort") continue;
            if (k === "minPrice" || k === "maxPrice") obj[k] = Number(v);
            else obj[k] = v;
        }
        return obj;
    }, [searchParams]);

    useEffect(() => {
        api.get("/categories")
            .then(({ data }) => setCategory(data.find((c) => c.slug === slug) || { name: slug, slug }))
            .catch(() => setCategory({ name: slug, slug }));
    }, [slug]);

    // Load all in-category products (for filter facets) once per slug
    useEffect(() => {
        api.get("/products", { params: { category: slug, limit: 100 } })
            .then(({ data }) => setAllProducts(data.items || []))
            .catch(() => setAllProducts([]));
    }, [slug]);

    // Load filtered products
    useEffect(() => {
        setLoading(true);
        const params = { category: slug, limit: 48, sort, ...filters };
        api.get("/products", { params })
            .then(({ data }) => {
                setItems(data.items || []);
                setTotal(data.total || 0);
            })
            .catch(() => {
                setItems([]);
                setTotal(0);
            })
            .finally(() => setLoading(false));
    }, [slug, sort, filters]);

    const updateFilters = (next) => {
        const sp = new URLSearchParams();
        Object.entries(next).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
        });
        if (sort && sort !== "new") sp.set("sort", sort);
        setSearchParams(sp, { replace: true });
    };

    const setSort = (s) => {
        const sp = new URLSearchParams(searchParams);
        if (s === "new") sp.delete("sort");
        else sp.set("sort", s);
        setSearchParams(sp, { replace: true });
    };

    const setSubType = (st) => {
        const sp = new URLSearchParams(searchParams);
        if (!st) sp.delete("subType");
        else sp.set("subType", st);
        setSearchParams(sp, { replace: true });
    };
    const activeSubType = searchParams.get("subType") || "";
    const subTypes = Array.isArray(category?.subTypes) ? category.subTypes : [];

    return (
        <div className="min-h-screen bg-ivory" data-testid="category-page">
            <Header />
            <main className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-10 pb-16">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
                    <div>
                        <span className="label-eyebrow">Collection</span>
                        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-inkDark tracking-tight" data-testid="category-title">
                            {category?.name || ""}
                        </h1>
                        <p className="mt-2 text-inkMuted">{loading ? "Loading…" : `${total} pieces`}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="label-eyebrow">Sort</label>
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="rounded-full bg-ivory border border-inkDark/10 px-4 py-2 text-sm focus:border-skyblueDeep outline-none"
                            data-testid="sort-select"
                        >
                            <option value="new">Newest</option>
                            <option value="priceAsc">Price: Low to High</option>
                            <option value="priceDesc">Price: High to Low</option>
                            <option value="bestSelling">Best Selling</option>
                        </select>
                    </div>
                </div>

                {subTypes.length ? (
                    <div className="flex flex-wrap gap-2 mb-6" data-testid="subtype-chips">
                        <button
                            type="button"
                            onClick={() => setSubType("")}
                            className={
                                "px-4 py-1.5 rounded-full text-sm border transition-colors " +
                                (!activeSubType
                                    ? "bg-skyblueDeep text-white border-skyblueDeep"
                                    : "bg-ivory text-inkDark border-inkDark/15 hover:border-skyblueDeep hover:text-skyblueDeep")
                            }
                            data-testid="subtype-chip-all"
                            aria-pressed={!activeSubType}
                        >
                            All
                        </button>
                        {subTypes.map((st) => (
                            <button
                                key={st}
                                type="button"
                                onClick={() => setSubType(st)}
                                className={
                                    "px-4 py-1.5 rounded-full text-sm border transition-colors " +
                                    (activeSubType === st
                                        ? "bg-skyblueDeep text-white border-skyblueDeep"
                                        : "bg-ivory text-inkDark border-inkDark/15 hover:border-skyblueDeep hover:text-skyblueDeep")
                                }
                                data-testid={`subtype-chip-${st.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                                aria-pressed={activeSubType === st}
                            >
                                {st}
                            </button>
                        ))}
                    </div>
                ) : null}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {filtersOn ? (
                        <div className="lg:col-span-3">
                            <FilterSidebar
                                products={allProducts}
                                filters={filters}
                                onChange={updateFilters}
                            />
                        </div>
                    ) : null}
                    <div className={filtersOn ? "lg:col-span-9" : "lg:col-span-12"}>
                        {!loading && items.length === 0 ? (
                            <div className="bg-beige rounded-2xl p-10 text-center text-inkMuted" data-testid="category-empty">
                                No pieces match your filters yet.
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 lg:gap-8">
                                {items.map((p) => (
                                    <ProductCard key={p.id} product={p} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
            <ChatbotWidget />
        </div>
    );
}
